
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { preloadPage, preloadImages } from '../../utils/preload';

const PreloadLink = ({ 
  children, 
  to, 
  className, 
  onMouseDown = null, 
  onClick = null,
  preloadOnMouseEnter = true,
  prefetchTimeout = 0, // Reduced to 0 for immediate preloading on hover
  scrollToTop = true,
  preloadImages: imagesToPreload = [],
  ...props 
}) => {
  const navigate = useNavigate();
  const [isHovering, setIsHovering] = useState(false);
  const [isPrefetching, setIsPrefetching] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  
  // Detect mobile devices on component mount
  useEffect(() => {
    const checkMobileDevice = () => {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
             (navigator.maxTouchPoints && navigator.maxTouchPoints > 2);
    };
    
    setIsMobileDevice(checkMobileDevice());
    
    // On mobile devices, preload important images and links immediately
    if (checkMobileDevice() && !isPrefetching && imagesToPreload && imagesToPreload.length > 0) {
      // For mobile, preload images immediately regardless of hover
      preloadImages(imagesToPreload, { compressionOptions: { quality: 90, critical: true } });
      
      // For mobile, preload the destination page immediately if the link is visible in viewport
      const observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          preloadPage(to);
          setIsPrefetching(true);
          observer.disconnect();
        }
      }, { threshold: 0.1 }); // Start preloading when 10% of the link is visible
      
      // Start observing once we have a reference to the link element
      setTimeout(() => {
        const linkElement = document.querySelector(`a[href="${to}"]`);
        if (linkElement) {
          observer.observe(linkElement);
        }
      }, 100);
    }
  }, [to, imagesToPreload, isPrefetching]);
  
  // Handle preloading on mouse enter immediately for desktop
  const handleMouseEnter = useCallback(() => {
    setIsHovering(true);
    
    if (preloadOnMouseEnter && !isPrefetching && !isMobileDevice) {
      // Immediate preload on hover for desktop
      preloadPage(to);
      
      // If there are specific images to preload, preload them immediately
      if (imagesToPreload && imagesToPreload.length > 0) {
        preloadImages(imagesToPreload, { compressionOptions: { quality: 90, critical: true } });
      }
      
      setIsPrefetching(true);
    }
  }, [to, preloadOnMouseEnter, isPrefetching, imagesToPreload, isMobileDevice]);
  
  // Handle mouse leave
  const handleMouseLeave = useCallback(() => {
    setIsHovering(false);
  }, []);
  
  // Handle mouse down for desktop
  const handleMouseDown = useCallback((e) => {
    // Call the original onMouseDown handler if provided
    if (onMouseDown) {
      onMouseDown(e);
    }
  }, [onMouseDown]);
  
  // Handle touch start - specific for mobile devices
  const handleTouchStart = useCallback(() => {
    if (!isPrefetching && isMobileDevice) {
      // Immediate preload on touch start for mobile
      preloadPage(to);
      
      if (imagesToPreload && imagesToPreload.length > 0) {
        preloadImages(imagesToPreload, { compressionOptions: { quality: 90, critical: true } });
      }
      
      setIsPrefetching(true);
    }
    
    // Call the original onMouseDown handler if provided
    if (onMouseDown) {
      onMouseDown();
    }
  }, [to, isPrefetching, imagesToPreload, isMobileDevice, onMouseDown]);
  
  // Handle click
  const handleClick = useCallback((e) => {
    if (onClick) {
      onClick(e);
    }
    
    // If it's an anchor link on the same page, don't prevent default
    if (to.startsWith('#')) {
      return;
    }
    
    // For hash links (anchor links to another page), handle navigation differently
    if (to.includes('#')) {
      e.preventDefault();
      navigate(to);
      return;
    }
    
    // For regular links, prevent default and handle navigation
    if (to && !to.startsWith('http')) {
      e.preventDefault();
      
      // Navigate immediately
      navigate(to);
      // Scroll to top after navigation if scrollToTop is true
      if (scrollToTop) {
        window.scrollTo(0, 0);
      }
    }
  }, [to, onClick, navigate, scrollToTop]);
  
  return (
    <Link 
      to={to} 
      className={className} 
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onClick={handleClick}
      {...props}
    >
      {children}
    </Link>
  );
};

export default PreloadLink;
